import { z } from "zod";
import { HEX_COLOR_PATTERN, ID_PATTERN, SECTION_TYPES, SECTION_VARIANTS, type SectionType } from "@baze/schema";

/**
 * Authoring formats for the Git-maintained catalog (designs/, templates/, prompts/, catalog/*.yaml)
 * and the compiled catalog shape consumed by the Go API, the app, and the web runtime.
 */

const slugId = z.string().regex(ID_PATTERN);
const hex = z.string().regex(HEX_COLOR_PATTERN);
const cssLength = z.string().regex(/^(?:0|[0-9]*\.?[0-9]+(?:rem|px|em))$/);
const cssShadow = z.string().max(200).regex(/^[0-9a-zA-Z.,()#%\s-]+$/);
const sectionType = z.enum(SECTION_TYPES as [SectionType, ...SectionType[]]);
/** `YYYY-MM-DD`. Frontmatter YAML turns unquoted dates into Date objects; normalise them back. */
const isoDate = z.preprocess(
  (v) => (v instanceof Date ? v.toISOString().slice(0, 10) : v),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
);

// ── catalog/industries.yaml ────────────────────────────────────────────────────
export const industrySchema = z.strictObject({
  id: slugId,
  name: z.string().min(1).max(60),
  group: z.enum(["fnb", "jasa", "hospitality", "profesional", "retail", "seni", "acara", "komunitas", "personal"]),
  icon: z.string().regex(/^[a-z0-9-]+$/), // lucide icon name
  schemaType: z.string().regex(/^[A-Za-z]+$/), // schema.org LocalBusiness subtype
  description: z.string().max(200),
  keywords: z.array(z.string().max(40)).max(20).default([]),
});
export const INDUSTRY_GROUP_LABELS = {
  fnb: "Makanan & Minuman",
  jasa: "Jasa",
  hospitality: "Penginapan",
  profesional: "Profesional",
  retail: "Toko & Retail",
  seni: "Seni & Budaya",
  acara: "Acara & Undangan",
  komunitas: "Komunitas & Organisasi",
  personal: "Personal & Kreator",
} as const;

// ── catalog/fonts.yaml ─────────────────────────────────────────────────────────
export const fontSchema = z.strictObject({
  id: slugId,
  family: z.string().min(1).max(60), // CSS family name as declared by the fontsource package
  package: z.string().regex(/^@fontsource-variable\/[a-z0-9-]+$/),
  category: z.enum(["sans", "serif", "display"]),
  fallback: z.string().max(120),
});
export const fontPairSchema = z.strictObject({
  id: slugId,
  label: z.string().max(60),
  heading: slugId,
  body: slugId,
});
export const fontsFileSchema = z.strictObject({ fonts: z.array(fontSchema), pairs: z.array(fontPairSchema) });

// ── catalog/stock-images.yaml ──────────────────────────────────────────────────
export const stockImageSchema = z.strictObject({
  key: z.string().regex(/^[a-z0-9-]+\/[a-z0-9-]+$/), // "<industry>/<name>"
  src: z.string().regex(/^https:\/\/images\.unsplash\.com\/photo-[A-Za-z0-9-]+$/),
  alt: z.string().min(1).max(200),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  credit: z.string().max(80).optional(),
});
export const stockImagesFileSchema = z.strictObject({ images: z.array(stockImageSchema) });

// ── designs/<id>/DESIGN.md frontmatter ────────────────────────────────────────
/** Animation personalities a theme can pick; the engine maps each to CSS presets. */
export const MOTION_STYLES = ["subtle", "cinematic", "playful", "editorial", "dreamy", "bold", "kinetic", "parallax"] as const;
export type MotionStyle = (typeof MOTION_STYLES)[number];

/** Atmosphere layered behind the whole site (CSS only). Omitted = none. */
export const TEXTURES = ["none", "grain", "grid", "dots", "aurora"] as const;
export type Texture = (typeof TEXTURES)[number];

/** Indonesian labels for motion styles and textures (theme cards, filters, admin). */
export const MOTION_LABELS: Record<MotionStyle, string> = {
  subtle: "Halus",
  cinematic: "Sinematik",
  playful: "Ceria",
  editorial: "Editorial",
  dreamy: "Dreamy",
  bold: "Tegas",
  kinetic: "Kinetik",
  parallax: "Parallax",
};
export const TEXTURE_LABELS: Record<Texture, string> = { none: "Polos", grain: "Grain", grid: "Garis kisi", dots: "Titik", aurora: "Aurora" };

export const designTokensSchema = z.strictObject({
  colors: z.strictObject({
    primary: hex,
    onPrimary: hex,
    secondary: hex,
    accent: hex,
    background: hex,
    surface: hex,
    text: hex,
    muted: hex,
    border: hex,
    inverted: hex,
    onInverted: hex,
  }),
  radius: z.strictObject({ sm: cssLength, md: cssLength, lg: cssLength, button: cssLength }),
  shadow: z.strictObject({ sm: cssShadow, md: cssShadow, lg: cssShadow }),
  spacing: z.enum(["compact", "normal", "airy"]),
  heading: z.strictObject({
    weight: z.number().int().min(300).max(900),
    case: z.enum(["normal", "uppercase"]),
    tracking: z.string().regex(/^-?[0-9]*\.?[0-9]+em$|^0$/),
  }),
  /** Motion style (CSS-only, live sites). Omitted = "subtle", the engine default. */
  motion: z.enum(MOTION_STYLES).optional(),
  /** Background atmosphere. Omitted = "none". */
  texture: z.enum(TEXTURES).optional(),
});

const variantDefaults = z
  .record(z.string(), z.string())
  .superRefine((map, ctx) => {
    for (const [type, variant] of Object.entries(map)) {
      const allowed = (SECTION_VARIANTS as Record<string, readonly string[]>)[type];
      if (!allowed) ctx.addIssue({ code: "custom", path: [type], message: `unknown section type "${type}"` });
      else if (!allowed.includes(variant))
        ctx.addIssue({ code: "custom", path: [type], message: `variant "${variant}" not allowed for ${type} (${allowed.join(", ")})` });
    }
  });

export const designFrontmatterSchema = z.strictObject({
  id: slugId,
  version: z.number().int().min(1),
  name: z.string().min(1).max(60),
  description: z.string().min(1).max(200),
  engine: z.string().regex(/^[<>=0-9 .]+$/),
  /** Seed price in rupiah (one-time, per website). 0 = free. Admin overlay overrides at runtime. */
  price: z.number().int().min(0).max(100_000_000),
  /** Release date; drives the "Baru" badge for a few weeks (see catalogBadges). */
  added: isoDate.optional(),
  industries: z.array(slugId).min(1),
  fontPair: slugId,
  tokens: designTokensSchema,
  variants: variantDefaults,
});

/** Prose sections of DESIGN.md, keyed by normalized H2 heading. */
export const DESIGN_SECTIONS = {
  karakter: { heading: "Karakter", required: true, max: 1500 },
  warna: { heading: "Warna", required: false, max: 1500 },
  tipografi: { heading: "Tipografi", required: false, max: 1500 },
  komponen: { heading: "Komponen & varian", required: false, max: 2000 },
  foto: { heading: "Foto & gambar", required: false, max: 1500 },
  doDont: { heading: "Do / Don't", required: false, max: 2000 },
  suara: { heading: "Suara", required: true, max: 600 },
} as const;

// ── templates/<industry>/<id>/TEMPLATE.md frontmatter ─────────────────────────
/**
 * Template sections use the real section content schema, with two authoring conveniences:
 * - image `src: "stock:<industry>/<name>"` resolves from stock-images.yaml (alt/size filled in),
 * - placeholders {{nama_usaha}}, {{kota}}, {{kategori}} are substituted at instantiation.
 * `id` is generated (`<type>-<n>`); `anchor` defaults per type; `variant` falls back to the design default.
 */
export const templateSectionSchema = z.strictObject({
  type: sectionType,
  variant: z.string().optional(),
  anchor: slugId.optional(),
  navLabel: z.string().max(30).optional(),
  showInNav: z.boolean().optional(),
  tone: z.enum(["default", "surface", "inverted"]).optional(),
  content: z.record(z.string(), z.unknown()),
});

export const templatePageSchema = z.strictObject({
  id: slugId,
  slug: z.string(),
  title: z.string().min(1).max(80),
  seo: z.strictObject({ title: z.string().max(90), description: z.string().max(200) }),
  sections: z.array(templateSectionSchema).min(1),
});

/** Style tags for the template gallery filter ("Gaya"), like an invitation catalogue. */
export const TEMPLATE_STYLES = ["adat", "islami", "elegan", "floral", "minimalis", "vintage", "ceria", "animasi"] as const;
export type TemplateStyle = (typeof TEMPLATE_STYLES)[number];
export const TEMPLATE_STYLE_LABELS: Record<TemplateStyle, string> = {
  adat: "Adat & Tradisional",
  islami: "Islami",
  elegan: "Elegan & Mewah",
  floral: "Floral & Bunga",
  minimalis: "Minimalis",
  vintage: "Vintage & Retro",
  ceria: "Ceria & Playful",
  animasi: "Animasi & Efek",
};

export const templateFrontmatterSchema = z.strictObject({
  id: slugId,
  version: z.number().int().min(1),
  name: z.string().min(1).max(60),
  industry: slugId,
  description: z.string().min(1).max(200),
  /** Seed price in rupiah (one-time, per website). 0 = free. Admin overlay overrides at runtime. */
  price: z.number().int().min(0).max(100_000_000),
  /** Release date; drives the "Baru" badge for a few weeks (see catalogBadges). */
  added: isoDate.optional(),
  designs: z.array(slugId).min(1),
  /** Gallery style tags (optional, at most 3). */
  styles: z.array(z.enum(TEMPLATE_STYLES)).max(3).default([]),
  site: z
    .strictObject({
      tagline: z.string().max(160).optional(),
      hours: z.array(z.unknown()).optional(),
      whatsapp: z.strictObject({ defaultMessage: z.string().max(300) }).optional(),
    })
    .default({}),
  pages: z.array(templatePageSchema).min(1),
  brief: z.strictObject({
    offeringsLabel: z.string().max(40),
    offeringExamples: z.array(z.string().max(60)).max(12),
    descriptionExamples: z.array(z.string().max(200)).max(6),
    audienceExamples: z.array(z.string().max(60)).max(8).default([]),
  }),
});

export const TEMPLATE_SECTIONS = {
  panduanAi: { heading: "Panduan AI", required: true, max: 1200 },
  catatan: { heading: "Catatan", required: false, max: 3000 },
} as const;

// ── prompts/*.md ───────────────────────────────────────────────────────────────
export const promptFrontmatterSchema = z.strictObject({
  id: z.enum(["generate", "rewrite", "seo"]),
  version: z.number().int().min(1),
  temperature: z.number().min(0).max(2),
  maxTokens: z.number().int().min(16).max(4096),
  description: z.string().max(200),
});
export const PROMPT_SECTIONS = {
  system: { heading: "System", required: true, max: 6000 },
  user: { heading: "User", required: true, max: 6000 },
} as const;

// ── catalog/site-plans.yaml (seed only; Paket Website tiers are edited in admin afterwards) ──
export const sitePlanLimitsSchema = z.strictObject({
  customDomain: z.boolean(),
  removeBranding: z.boolean(),
  /** Manage DNS records on domains bought through Baze (e.g. shop → another app). */
  customDns: z.boolean().default(false),
  pagesPerSite: z.number().int().min(1),
  aiGenerationsPerMonth: z.number().int().min(0),
  aiRewritesPerMonth: z.number().int().min(0),
  storageMb: z.number().int().min(0),
  analyticsDays: z.number().int().min(0),
});
export const sitePlanSeedSchema = z.strictObject({
  id: slugId,
  name: z.string().max(40),
  description: z.string().max(200),
  /** Rupiah per year, per website. 0 = free tier (exactly one plan must be free). */
  priceYear: z.number().int().min(0),
  sort: z.number().int(),
  highlighted: z.boolean().default(false),
  features: z.array(z.string().max(80)).max(12),
  limits: sitePlanLimitsSchema,
});
export const sitePlansFileSchema = z.strictObject({ plans: z.array(sitePlanSeedSchema).min(1) });

// ── catalog/tlds.yaml (seed; markup editable in admin) ────────────────────────
export const tldSchema = z.strictObject({
  tld: z.string().regex(/^[a-z0-9]+(\.[a-z0-9]+)*$/),
  label: z.string().max(60),
  /** Shown first in domain suggestions, in this order. */
  suggest: z.boolean(),
  maxYears: z.number().int().min(1).max(10),
  /** Markup applied on top of the registrar's price. */
  markupPercent: z.number().min(0).max(500),
  markupFixed: z.number().int().min(0),
  /** Price rounding step in rupiah (e.g. 1000 → Rp 181.000). */
  roundTo: z.number().int().min(1),
  requiresDocuments: z.boolean(),
  documentsNote: z.string().max(200).optional(),
  /** Fallback price per year used by the dummy registrar and when the registrar is unreachable. */
  fallbackPrice: z.number().int().min(0),
});
export const tldsFileSchema = z.strictObject({ tlds: z.array(tldSchema).min(1) });

// ── AI content brief: the ONLY structure the LLM produces ─────────────────────
export const contentBriefSchema = z.strictObject({
  tagline: z.string().max(160),
  hero: z.strictObject({ eyebrow: z.string().max(60), title: z.string().min(1).max(120), description: z.string().max(320) }),
  about: z.strictObject({ title: z.string().min(1).max(120), body: z.string().max(900) }),
  offerings: z.array(z.strictObject({ name: z.string().min(1).max(80), description: z.string().max(240) })).max(12),
  faq: z.array(z.strictObject({ question: z.string().min(1).max(160), answer: z.string().min(1).max(500) })).max(6),
  cta: z.strictObject({ title: z.string().min(1).max(120), description: z.string().max(320) }),
  seo: z.strictObject({ title: z.string().min(1).max(70), description: z.string().min(1).max(170) }),
});
export type ContentBrief = z.infer<typeof contentBriefSchema>;

export const rewriteOutputSchema = z.strictObject({ text: z.string().min(1).max(1500) });
export const seoOutputSchema = z.strictObject({ title: z.string().min(1).max(70), description: z.string().min(1).max(170) });

// ── Compiled shapes ────────────────────────────────────────────────────────────
export type Industry = z.infer<typeof industrySchema>;
export type Font = z.infer<typeof fontSchema>;
export type FontPair = z.infer<typeof fontPairSchema>;
export type StockImage = z.infer<typeof stockImageSchema>;
export type DesignTokens = z.infer<typeof designTokensSchema>;
export type SitePlanSeed = z.infer<typeof sitePlanSeedSchema>;
export type SitePlanLimits = z.infer<typeof sitePlanLimitsSchema>;
export type Tld = z.infer<typeof tldSchema>;

export interface Design extends Omit<z.infer<typeof designFrontmatterSchema>, "variants"> {
  variants: Partial<Record<SectionType, string>>;
  docs: Partial<Record<keyof typeof DESIGN_SECTIONS, string>>;
  voice: string;
  source: string;
}

export interface TemplateSection {
  id: string;
  type: SectionType;
  variant?: string;
  anchor: string;
  navLabel?: string;
  showInNav?: boolean;
  tone?: "default" | "surface" | "inverted";
  content: Record<string, unknown>;
}

export interface Template extends Omit<z.infer<typeof templateFrontmatterSchema>, "pages"> {
  pages: Array<Omit<z.infer<typeof templatePageSchema>, "sections"> & { sections: TemplateSection[] }>;
  aiGuide: string;
  notes: string;
  source: string;
}

export interface Prompt extends z.infer<typeof promptFrontmatterSchema> {
  system: string;
  user: string;
  source: string;
}

export interface CompiledCatalog {
  /** sha256 of the compiled content (without this field); used as ETag and in ai_jobs. */
  hash: string;
  engine: 1;
  industries: Industry[];
  fonts: Font[];
  fontPairs: FontPair[];
  designs: Design[];
  templates: Template[];
  stockImages: StockImage[];
  prompts: Prompt[];
  sitePlans: SitePlanSeed[];
  tlds: Tld[];
  /** Allowed variants per section type, first = engine default. Lets Go resolve variants without TS. */
  sectionVariants: Record<SectionType, readonly string[]>;
  /** Default anchor per section type (Indonesian slugs). */
  sectionAnchors: Record<SectionType, string>;
  /** JSON Schemas for LLM structured output (Ollama `format`) and validation. */
  schemas: { contentBrief: unknown; rewrite: unknown; seo: unknown };
}

export const DEFAULT_ANCHORS: Record<SectionType, string> = {
  navbar: "top",
  hero: "beranda",
  about: "tentang",
  services: "layanan",
  menu: "menu",
  products: "produk",
  gallery: "galeri",
  testimonials: "testimoni",
  faq: "faq",
  stats: "angka",
  "opening-hours": "jam-buka",
  location: "lokasi",
  contact: "kontak",
  pricing: "harga",
  cta: "hubungi",
  timeline: "alur",
  events: "acara",
  team: "tim",
  quote: "kutipan",
  countdown: "hitung-mundur",
  gift: "hadiah",
  links: "tautan",
  picks: "rekomendasi",
  footer: "footer",
};

export const PLACEHOLDERS = ["nama_usaha", "kota", "kategori"] as const;
/**
 * Input limits for placeholder values. The compiler instantiates every template with values of exactly
 * these lengths, so any business name/city within limits always yields a valid definition.
 * The wizard and the Go API enforce the same limits.
 */
export const BRIEF_LIMITS = { nameMax: 40, cityMax: 30, descriptionMax: 600, audienceMax: 120, offeringsMax: 12, offeringNameMax: 60 } as const;
/** Keys whose values keep placeholders for render-time substitution (WhatsApp messages). */
export const RENDER_TIME_KEYS = ["message", "orderMessage", "defaultMessage"] as const;
